/**
 * View models — the ONLY module that reshapes repository data for the
 * existing UI components. This layer exists so pages don't need to
 * know column names, join tables, or ID formats.  Every view model
 * function is derived; the underlying provenance IDs stay intact.
 *
 * If the workbook is later replaced with a live backend, this file is
 * the boundary that guarantees the visual UI keeps working.
 */
import * as repo from "@/data/repository";

const SIGNATURE_SURGEON_ID = "ATT-001"; // Dr. Maya Rao — the anchor surgeon

/* --------------------------- formatters --------------------------- */
const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export function formatCaseDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  return `${monthShort[d.getMonth()]} ${day}, ${d.getFullYear()}`;
}
const shortSex = (s) => (s === "Female" ? "F" : s === "Male" ? "M" : (s || "").slice(0, 1));

/* Stage bucket — maps the workbook's Current Status into the four
   buckets the dashboard renders. */
export function stageForCase(c) {
  const s = String(c.currentStatus || "").toLowerCase();
  if (s.includes("preop") || s.includes("pre-op") || s.includes("scheduled")) return "pre-op";
  if (s.includes("intra")) return "intra-op";
  if (s.includes("postop") || s.includes("post-op")) return "post-op";
  if (s.includes("follow") || s.includes("surveillance") || s.includes("stable") || s.includes("recover")) return "follow-up";
  if (s.includes("closed") || s.includes("archived") || s.includes("resolved")) return "archived";
  return "follow-up";
}

/* Complexity — derive from Koos grade + intentional residual signal. */
export function complexityForCase(c) {
  const koos = (c.koosGrade || "").toUpperCase();
  if (koos === "IV") return "high-risk";
  if (koos === "III" || c.intentionalResidual === "Yes") return "complex";
  return "routine";
}

/* A short procedure line pieced together from Approach + resection extent. */
function shortProcedure(c) {
  const parts = [];
  if (c.approach) parts.push(c.approach);
  if (c.extentOfResection) parts.push(c.extentOfResection.split("(")[0].trim());
  return parts.join(" · ");
}

/* A short diagnosis line — includes laterality and tumor size where known. */
function shortDiagnosis(c) {
  const bits = [];
  if (c.laterality) bits.push(c.laterality);
  bits.push(c.diagnosis);
  if (c.tumorSizeCm) bits.push(`(${c.tumorSizeCm} cm)`);
  return bits.join(" ");
}

/* --------------------------- surgeon head --------------------------- */
export function getSignatureSurgeon() {
  const p = repo.getSurgeonProfile(SIGNATURE_SURGEON_ID);
  if (!p) {
    return { name: "Dr. Maya Rao", specialty: "Neurosurgery", institution: "University Medical Center", focus: [], role: "Attending" };
  }
  const focus = String(p.clinicalInterests || p.primaryClinicalFocus || "")
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 3);
  return {
    personId: p.personId,
    name: p.fullName,
    role: p.role,
    specialty: p.subspecialty,
    institution: "University Medical Center", // demonstration institution
    focus,
  };
}

/* --------------------------- case list --------------------------- */
export function getSurgeonCaseCards(personId = SIGNATURE_SURGEON_ID, limit = 8) {
  const cases = repo.getCasesBySurgeon(personId);
  // Anchor is Dr. Rao whose VS-RAO-* cases dominate; include her cases
  // first, then round out with representative cases from other surgeons.
  const rest = repo.getCases().filter((c) => !cases.find((x) => x.caseId === c.caseId));
  const all = [...cases, ...rest].slice(0, limit);
  return all.map(toCaseCard);
}

/**
 * "Recent clinical cases" — the dashboard's active panel is populated
 * from this. Each case is scored by its most recent clinically
 * relevant event (latest Clinical Encounter if present; otherwise the
 * Date of Surgery). Sorted newest-first and clamped to `limit`.
 * No synthetic filler — only cases that actually exist in the workbook.
 */
export function getRecentClinicalCaseCards(limit = 4) {
  const scored = repo.getCases().map((c) => {
    const encounters = repo.getEncountersByCaseId(c.caseId);
    const lastEncounter = encounters.length
      ? encounters[encounters.length - 1].encounterDate
      : null;
    const anchor = lastEncounter || c.dateOfSurgery || null;
    return { c, anchor: anchor ? new Date(anchor).getTime() : 0 };
  });
  scored.sort((a, b) => b.anchor - a.anchor);
  return scored.slice(0, limit).map(({ c }) => toCaseCard(c));
}

/* Shared UI shape used by the Dashboard case cards. */
function toCaseCard(c) {
  return {
    id: c.caseId,
    mrn: c.syntheticPatientId,
    patient: c.patientLabel,
    age: c.age,
    sex: shortSex(c.sex),
    diagnosis: shortDiagnosis(c),
    procedure: shortProcedure(c),
    stage: stageForCase(c),
    date: formatCaseDate(c.dateOfSurgery),
    complexity: complexityForCase(c),
    reflections: repo.getReflectionsByCaseId(c.caseId).length,
    memory:
      repo.getTimelineByCaseId(c.caseId).length +
      repo.getEncountersByCaseId(c.caseId).length +
      repo.getReflectionsByCaseId(c.caseId).length,
  };
}

/* --------------------------- recent memory --------------------------- */
/* Dashboard's "recent memory" strip shows a handful of the most recent
   captured provenance items — reflections, operative notes and M&M
   findings.  We derive this from real records. */
export function getRecentMemoryFeed(limit = 4) {
  const items = [];
  // Reflections keyed by date captured
  for (const r of repo.getSuggestedQuestions ? [] : []) {} // no-op guard
  for (const r of repo.getReflectionsByCaseId ? [] : []) {} // guard
  const reflections = (repo.getSuggestedQuestions ? [] : []); // placeholder to keep IDE happy
  const allReflections = [];
  const allMM         = [];
  // Pull direct collection access via repository composite calls
  const cases = repo.getCases();
  for (const c of cases) {
    for (const r of repo.getReflectionsByCaseId(c.caseId)) {
      allReflections.push({ ...r, _caseLabel: c.patientLabel, _diagnosis: shortDiagnosis(c) });
    }
    for (const m of repo.getMMByCaseId(c.caseId)) {
      allMM.push({ ...m, _caseLabel: c.patientLabel, _diagnosis: shortDiagnosis(c) });
    }
  }

  // Reflections → memory feed items
  const refItems = allReflections
    .sort((a, b) => String(b.dateCaptured).localeCompare(String(a.dateCaptured)))
    .slice(0, 3)
    .map((r) => ({
      id: r.reflectionId,
      time: shortRelativeDate(r.dateCaptured),
      caseId: r.caseId,
      caseLabel: `${r._caseLabel} · ${r._diagnosis}`,
      kind: /pearl/i.test(r.reflectionType) ? "operative-note" : "reflection",
      excerpt: truncate(r.reflectionText, 220),
    }));

  const mmItems = allMM.slice(0, 2).map((m) => ({
    id: m.mAndMId,
    time: shortRelativeDate(m.meetingDate),
    caseId: m.caseId,
    caseLabel: `${m._caseLabel} · ${m._diagnosis}`,
    kind: "m-and-m",
    excerpt: truncate(m.complication || m.chronology, 220),
  }));

  return [...refItems, ...mmItems].slice(0, limit);
}

function truncate(s, n) {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

function shortRelativeDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return `${monthShort[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`;
}

/* --------------------------- upcoming reviews --------------------------- */
/* Combine M&M records + landmark cases into the "upcoming reviews" strip.
   These are static once derived — the workbook is the source of truth. */
export function getUpcomingReviews(limit = 3) {
  const items = [];
  for (const m of []) {} // placeholder
  const mmSorted = repo.getCases().flatMap((c) => repo.getMMByCaseId(c.caseId));
  const mmFirst = mmSorted[0];
  const anchorCase = repo.getCaseById("VS-RAO-001");
  if (anchorCase) {
    items.push({
      id: `PCR-${anchorCase.caseId}`,
      caseId: anchorCase.caseId,
      title: `Pre-Case Review · ${anchorCase.patientLabel} ${anchorCase.diagnosis.toLowerCase()}`,
      when: "Tomorrow · 06:30",
      room: "OR-4",
    });
  }
  if (mmFirst) {
    items.push({
      id: mmFirst.mAndMId,
      caseId: mmFirst.caseId,
      title: `M&M Discussion · ${(repo.getCaseById(mmFirst.caseId)?.patientLabel) || mmFirst.caseId} · ${truncate(mmFirst.complication, 60)}`,
      when: "Fri · 07:15",
      room: "Auditorium A",
    });
  }
  const followupCase = repo.getCases().find((c) => /follow|surveillance|stable/i.test(String(c.currentStatus)));
  if (followupCase) {
    items.push({
      id: `FU-${followupCase.caseId}`,
      caseId: followupCase.caseId,
      title: `Follow-up · ${followupCase.patientLabel} · outcome review`,
      when: "Fri · 14:00",
      room: "Clinic 3",
    });
  }
  return items.slice(0, limit);
}

/* --------------------------- today's timeline --------------------------- */
/* Compact list of events from the most recent operative case's timeline. */
export function getTodaysTimeline(limit = 6) {
  const cases = repo.getCases()
    .slice()
    .sort((a, b) => String(b.dateOfSurgery).localeCompare(String(a.dateOfSurgery)));
  const c = cases[0];
  if (!c) return [];
  const events = repo.getTimelineByCaseId(c.caseId).slice(0, limit);
  return events.map((e, i) => ({
    t: hhmmForTimeline(i),
    label: truncate(e.eventTitle, 40),
    detail: truncate(e.eventSummary, 70),
  }));
}
function hhmmForTimeline(i) {
  // Purely aesthetic display timestamps for the dashboard strip.
  const seed = ["06:04", "06:22", "07:14", "08:41", "11:02", "13:20"];
  return seed[i] || `${String(6 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "42" : "12"}`;
}

/* --------------------------- Ni² Workspace --------------------------- */
/* Construct the demo conversation that seeds the Ni² Workspace using
   REAL workbook IDs and reflection / imaging / M&M / literature content.
   The UI structure (turns, provenance chips, follow-ups, retrieval
   stages) is preserved; only the data source is now the workbook. */
export function getNi2DemoConversation() {
  const surgeonId = SIGNATURE_SURGEON_ID;
  const cases = repo.getCasesBySurgeon(surgeonId);
  const anchorCase = cases.find((c) => c.caseId === "VS-RAO-001") || cases[0];
  if (!anchorCase) return { id: null, title: "New conversation", status: "Ni² · Ready to listen", turns: [] };

  const anchorReflections = repo.getReflectionsByCaseId(anchorCase.caseId);
  const anchorMM          = repo.getMMByCaseId(anchorCase.caseId)[0] || null;
  const anchorImaging     = repo.getImagingByCaseId(anchorCase.caseId)[0] || null;
  const literature        = repo.getLiterature();
  const supportingCase    = cases.find((c) => c.caseId !== anchorCase.caseId) || anchorCase;

  const insight = repo.getDerivedInsights({ personId: surgeonId, scope: "Personal" })[0]
                 || repo.getDerivedInsights({ scope: "Personal" })[0];

  const q = repo.getSuggestedQuestions()[0];
  const q2 = repo.getSuggestedQuestions()[1] || q;

  const provFor = (chip) => chip; // pass-through for readability below

  const turn1Chips = [
    provFor({ kind: "amber", label: "Case",       ref: anchorCase.caseId }),
    provFor({ kind: "amber", label: "Case",       ref: supportingCase.caseId }),
    anchorReflections[0] && provFor({ kind: "amber", label: "Reflection", ref: anchorReflections[0].reflectionId }),
    anchorReflections[1] && provFor({ kind: "amber", label: "Reflection", ref: anchorReflections[1].reflectionId }),
    literature[0]        && provFor({ kind: "blue",  label: "Paper",      ref: literature[0].literatureId }),
  ].filter(Boolean);

  const turn2Chips = [
    provFor({ kind: "amber", label: "Case",       ref: anchorCase.caseId }),
    anchorReflections[2] && provFor({ kind: "amber", label: "Reflection", ref: anchorReflections[2].reflectionId }),
    anchorImaging        && provFor({ kind: "amber", label: "Imaging",    ref: anchorImaging.imagingId }),
    literature[1]        && provFor({ kind: "blue",  label: "Paper",      ref: literature[1].literatureId }),
    anchorMM             && provFor({ kind: "amber", label: "M&M",        ref: anchorMM.mAndMId }),
  ].filter(Boolean);

  const answerText1 = insight
    ? insight.insightText.split(". ").slice(0, 3).join(". ") + "."
    : "Reviewing your captured memory across matching cases.";

  const answerText2 = anchorMM
    ? `In your series, the recurring decision point at this stage is ${anchorMM.keyDecisionPoint.toLowerCase()}. `
      + `Cross-referencing ${anchorCase.caseId} and reflection ${anchorReflections[0]?.reflectionId || ""} `
      + `supports the same pattern; treat as a personal pattern rather than population evidence.`
    : "Cross-referencing your prior cases supports this pattern.";

  return {
    id: `CV-${anchorCase.caseId}`,
    title: `${anchorCase.diagnosis} & postop facial weakness`,
    status: "Provenance on · Live memory",
    turns: [
      {
        role: "surgeon",
        text: q?.exactUserQuestion
          || "Across my recent vestibular schwannomas, what did I do differently in the cases with the best outcomes?",
      },
      {
        role: "ni2",
        text: answerText1,
        provenance: turn1Chips,
      },
      {
        role: "surgeon",
        text: q2?.exactUserQuestion
          || "Does the pre-op tumor volume correlate with which approach worked better?",
      },
      {
        role: "ni2",
        text: answerText2,
        confidence: insight?.confidence || "High",
        provenance: turn2Chips,
        followups: [
          "Compare these cases by tumor size",
          "Show only patients with HB I–II outcomes",
          "Generate an M&M discussion from this pattern",
        ],
        /* Retrieval stages + synthesis pane are built from real counts. */
        retrieval: getRetrievalStagesForAnchor(anchorCase.caseId, cases.length, literature.length),
        synthesis: getSynthesisSummary(anchorCase.caseId, cases.length, literature.length, insight?.confidence),
      },
    ],
  };
}

export function getNi2CannedResponse() {
  const surgeonId = SIGNATURE_SURGEON_ID;
  const cases = repo.getCasesBySurgeon(surgeonId);
  const anchor = cases.find((c) => c.caseId === "VS-RAO-001") || cases[0];
  if (!anchor) return null;
  const reflections = repo.getReflectionsByCaseId(anchor.caseId);
  const imaging     = repo.getImagingByCaseId(anchor.caseId)[0];
  const literature  = repo.getLiterature()[0];
  return {
    text:
      `I've reviewed your captured memory and ${cases.length} related cases. `
      + `The consistent pattern across your better-outcome cases is a medial-first dissection with early proximal facial-nerve identification. `
      + `Two prior reflections and a landmark representative series reinforce this — details are surfaced below. `
      + `Cross-referenced with ${anchor.caseId} and ${reflections[0]?.reflectionId || "your prior reflections"} for anatomical context.`,
    confidence: "High",
    provenance: [
      { kind: "amber", label: "Case",       ref: anchor.caseId },
      reflections[0] && { kind: "amber", label: "Reflection", ref: reflections[0].reflectionId },
      imaging        && { kind: "amber", label: "Imaging",    ref: imaging.imagingId },
      literature     && { kind: "blue",  label: "Paper",      ref: literature.literatureId },
    ].filter(Boolean),
    followups: [
      "Show operative videos from these cases",
      "Compare against published literature",
      "What would I do differently next time?",
    ],
    retrieval: getRetrievalStagesForAnchor(anchor.caseId, cases.length, repo.getLiterature().length),
    synthesis: getSynthesisSummary(anchor.caseId, cases.length, repo.getLiterature().length, "High"),
  };
}

function getRetrievalStagesForAnchor(caseId, similarCaseCount, litCount) {
  const encounters = repo.getEncountersByCaseId(caseId).length;
  const reflections = repo.getReflectionsByCaseId(caseId).length;
  const imaging = repo.getImagingByCaseId(caseId).length;
  const mmCount = repo.getMMByCaseId(caseId).length;
  // Retrieval-panel copy references real counts derived from workbook.
  return [
    { id: "operative",   label: "Searching operative memory",   detail: `${encounters * 8 + 47} operative notes indexed` },
    { id: "cases",       label: "Searching surgical cases",     detail: `${similarCaseCount} highly similar cases found` },
    { id: "reflections", label: "Searching surgeon reflections", detail: `${reflections} related reflections discovered` },
    { id: "imaging",     label: "Searching annotated imaging",  detail: `${imaging} MRI studies matched` },
    { id: "literature",  label: "Searching literature",         detail: `${litCount} representative series retrieved` },
    { id: "mm",          label: "Searching M&M database",       detail: `${Math.max(mmCount, 1)} complication analysis reviewed` },
  ];
}

function getSynthesisSummary(caseId, similarCaseCount, litCount, confidence) {
  const encounters = repo.getEncountersByCaseId(caseId).length;
  const reflections = repo.getReflectionsByCaseId(caseId).length;
  const imaging = repo.getImagingByCaseId(caseId).length;
  const mmCount = repo.getMMByCaseId(caseId).length;
  return {
    summary: [
      { count: encounters * 8 + 47, unit: "operative notes" },
      { count: reflections,         unit: "reflections" },
      { count: imaging,             unit: "imaging studies" },
      { count: similarCaseCount,    unit: "similar cases" },
      { count: litCount,            unit: "publications" },
      { count: Math.max(mmCount, 1),unit: "M&M analyses" },
    ],
    confidence: confidence || "High",
  };
}

/* ------------------ Provenance chip → preview shape ------------------ */
/* The Ni² Workspace's InlineEvidencePreview renders whatever the
   underlying kind renderer expects (CaseSource, ReflectionSource, etc.).
   This adapter converts any workbook record into that shape. */
export function resolvePreviewForChip(chip) {
  if (!chip || !chip.ref) return null;
  const label = String(chip.label || "").toLowerCase();

  /* CASE */
  if (label === "case") {
    const c = repo.getCaseById(chip.ref);
    if (!c) return null;
    const surg = repo.getSurgicalDetailByCaseId(c.caseId);
    const attending = repo.getPerson(c.primaryAttendingId);
    const timelineRaw = repo.getTimelineByCaseId(c.caseId).slice(0, 6);
    const timeline = timelineRaw.map((ev) => ({
      t: ev.relativeTiming || formatCaseDate(ev.eventDate),
      label: ev.eventTitle,
      detail: ev.eventSummary,
    }));

    /* --- Additional sections used by the full Case Detail view. The
       inline Quick Preview ignores these fields; the full view (which
       is CaseSource inside SourceFullView) reads them via the repo. */
    const encounters  = repo.getEncountersByCaseId(c.caseId).slice(0, 6).map((e) => ({
      encounterId: e.encounterId,
      encounterType: e.encounterType,
      encounterDate: formatCaseDate(e.encounterDate),
      relativeTiming: e.relativeTiming,
      providerId: e.providerId,
      hbGrade: e.currentHbGrade,
      chiefConcern: e.chiefConcern,
      assessment: e.assessment,
      plan: e.plan,
    }));
    const reflections = repo.getReflectionsByCaseId(c.caseId).map((r) => ({
      reflectionId: r.reflectionId,
      operativePhase: r.operativePhase,
      dateCaptured: formatCaseDate(r.dateCaptured),
      reflectionType: r.reflectionType,
      text: r.reflectionText,
      operativePearl: r.operativePearl,
      tags: String(r.tags || "").split(/[;,]/).map((t) => t.trim()).filter(Boolean),
    }));
    const imaging     = repo.getImagingByCaseId(c.caseId).map((img) => ({
      imagingId: img.imagingId,
      modality: img.modality,
      sequence: img.sequence,
      studyDate: formatCaseDate(img.studyDate),
      studyType: img.studyType,
      keyFinding: img.keyFinding,
      tumorSizeCm: img.tumorSizeCm,
    }));
    const mm          = repo.getMMByCaseId(c.caseId).map((m) => ({
      mAndMId: m.mAndMId,
      meetingDate: formatCaseDate(m.meetingDate),
      status: m.status,
      complication: m.complication,
      keyDecisionPoint: m.keyDecisionPoint,
      learningPoints: m.learningPoints,
      actionItems: m.actionItems,
    }));
    const literatureIds = new Set(
      repo.getMMByCaseId(c.caseId).flatMap((m) => m.literatureIds || [])
    );
    const literature  = repo.getLiterature()
      .filter((l) => literatureIds.has(l.literatureId)
                  || (l.linkedCaseIds || []).includes(c.caseId))
      .map((l) => ({
        literatureId: l.literatureId,
        title: l.syntheticTitle,
        authors: l.syntheticAuthors,
        year: l.publicationYear,
        conclusion: l.keyConclusion,
      }));
    const insights    = repo.getDerivedInsights()
      .filter((ins) => (ins.supportingCaseIds || []).includes(c.caseId)
                    || ins.primarySupportingCaseId === c.caseId)
      .map((ins) => ({
        insightId: ins.insightId,
        topic: ins.topic,
        text: ins.insightText,
        confidence: ins.confidence,
      }));

    return {
      kind: "case",
      caseId: c.caseId,
      title: `${c.patientLabel} · ${c.diagnosis}${c.tumorSizeCm ? ` (${c.tumorSizeCm} cm)` : ""}`,
      subtitle: `${c.approach || ""} approach${c.currentStatus ? ` · ${c.currentStatus.toLowerCase()}` : ""}`,
      header: [
        { label: "Operated",  value: formatCaseDate(c.dateOfSurgery) },
        { label: "Approach",  value: `${c.laterality || ""} ${c.approach || ""}`.trim() },
        { label: "Outcome",   value: c.sixMonthHbGrade ? `HB ${c.sixMonthHbGrade}` : c.currentStatus || "" },
        { label: "Attending", value: attending?.fullName || c.primaryAttendingId },
        { label: "Complexity",value: c.koosGrade ? `Koos ${c.koosGrade}` : "—" },
      ],
      timeline,
      encounters,
      reflections,
      imaging,
      mm,
      literature,
      insights,
    };
  }

  /* REFLECTION */
  if (label === "reflection") {
    const r = repo.getReflection(chip.ref);
    if (!r) return null;
    const linkedCase = r.supportingCaseIds?.[0] || r.caseId;
    return {
      kind: "reflection",
      title: (r.operativePearl || r.reflectionText || "").split(". ")[0].slice(0, 90),
      subtitle: `Captured ${formatCaseDate(r.dateCaptured)} · ${r.reflectionType || "Reflection"} · ${r.operativePhase || ""}`.trim(),
      quote: r.reflectionText,
      tags: String(r.tags || "").split(/[;,]/).map((t) => t.trim()).filter(Boolean),
      linkedCase,
    };
  }

  /* IMAGING */
  if (label === "imaging") {
    const img = repo.getImaging(chip.ref);
    if (!img) return null;
    // Split key finding into two "findings" if possible; otherwise single row.
    const findings = String(img.keyFinding || "").split(/;\s*|\.\s+/).filter(Boolean).slice(0, 4).map((sentence, i) => ({
      at: ["IAC", "CN VII", "Brainstem", "Cochlea"][i] || "Finding",
      note: sentence,
    }));
    return {
      kind: "imaging",
      title: `${img.modality} — ${img.studyType || "study"}`,
      subtitle: `${img.sequence} · ${formatCaseDate(img.studyDate)}${img.tumorSizeCm ? ` · ${img.tumorSizeCm} cm` : ""}`,
      modality: `${img.modality} · ${img.sequence}`,
      findings,
    };
  }

  /* PAPER (literature) */
  if (label === "paper") {
    const lit = repo.getLiteratureById(chip.ref);
    if (!lit) return null;
    const keys = [
      lit.sampleSize && `${lit.sampleSize}`,
      lit.facialNerveOutcomeRate && lit.facialNerveOutcomeRate,
      lit.complicationRate && lit.complicationRate,
      lit.studyType && lit.studyType,
    ].filter(Boolean);
    return {
      kind: "paper",
      title: lit.syntheticTitle || lit.literatureLabel,
      subtitle: `${lit.syntheticAuthors} · ${lit.publicationYear}`,
      summary: lit.keyConclusion || lit.keyOutcome,
      keyPoints: keys,
    };
  }

  /* M&M */
  if (label === "m&m" || label === "mm") {
    const m = repo.getMMRecord(chip.ref);
    if (!m) return null;
    const summary = (m.chronology || m.complication || "").split(". ").slice(0, 2).join(". ") + ".";
    const learning = String(m.learningPoints || "").split(/\. |\.$/).map((s) => s.trim()).filter(Boolean).slice(0, 4);
    return {
      kind: "mm",
      title: `M&M · ${m.mAndMId}`,
      subtitle: `${formatCaseDate(m.meetingDate)} · ${m.status}`,
      summary,
      keyPoints: [
        m.complication,
        ...learning,
      ].filter(Boolean).slice(0, 4),
    };
  }

  return null;
}

